import { useEffect } from 'react';
import { useAtomValue } from 'jotai';
import { MyInfoState } from '@/recoil/atom';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';

export type LanguageSettingOptions = {
  /** 중국어 사용자에게 중국어 대신 영어를 보여줄지 (zh 번역본이 없는 화면용) */
  isZhEn: boolean;
  /** RN 이 외국어 모드로 열 때 붙이는 `?lang=en` 을 사용자 설정보다 우선할지 */
  rnLanguageIsFirst: boolean;
  /**
   * false 면 언어를 건드리지 않는다. 라우트마다 적용 여부가 갈리는 전역 호출
   * (`components/LanguageSync`)에서 훅을 조건부로 부를 수 없어 필요하다.
   */
  enabled?: boolean;
  /**
   * 사용자 설정이 ko 일 때 i18n 을 ko 로 되돌릴지. 라우트 단위 적용 지점
   * (`components/LanguageSync`)만 켠다 — 앞 화면이 en/zh 로 바꿔 둔 걸 되돌리는 건
   * 그 라우트를 소유한 쪽의 역할이다.
   *
   * `ErrorPage` 처럼 화면 안에서 잠깐 언어를 바꾸는 호출부가 켜면, 앱 외국어 모드로
   * 이미 en 이 적용된 상태를 ko 로 덮어써 버린다(404/403 만 한국어로 보이는 증상).
   */
  revertToKo?: boolean;
};

const useLanguageSetting = ({
  isZhEn,
  rnLanguageIsFirst,
  enabled = true,
  revertToKo = false,
}: LanguageSettingOptions) => {
  // pages router 의 정적 프리렌더 구간에서는 null 이 올 수 있어 옵셔널 체이닝 필수
  const searchParams = useSearchParams();
  const rnLanguage = searchParams?.get('lang');

  // 값을 쓰진 않고 effect 재실행 트리거로만 쓴다. `LanguageSync` 는 `_app` 에 있어
  // 라우트가 바뀌어도 언마운트되지 않는데, 이게 없으면 옵션이 같은 라우트끼리 이동할 때
  // (`/approval` → `/`, 둘 다 isZhEn:false) deps 가 안 바뀌어 effect 가 안 돈다. 그러면
  // `ErrorPage` 처럼 화면 언어를 en 으로 바꿔놓고 안 되돌리는 컴포넌트를 거친 뒤
  // 새로고침 전까지 en 이 굳는다. 예전엔 페이지마다 훅이 있어 마운트되며 되돌려줬다.
  const { pathname } = useRouter();

  const { language, passportId } = useAtomValue(MyInfoState);
  const { i18n } = useTranslation();

  useEffect(() => {
    if (!enabled) return;

    let settingLanguage = '';

    if (language === 'en') {
      settingLanguage = 'en';
    } else if (language === 'zh') {
      settingLanguage = isZhEn ? 'en' : 'zh';
    } else if (language === 'ko' && revertToKo && passportId) {
      // 싱글턴 i18n 이 en/zh 로 바뀐 채 남아 있을 수 있어 ko 도 명시적으로 되돌린다.
      // 언어를 ko 로 바꾸고 저장하면 reload 직후 localStorage 의 이전 값(en/zh)이 먼저
      // 적용되는데, 뒤이어 도착한 passport 응답이 ko 여도 여기서 안 되돌리면 그대로 굳는다.
      //
      // `passportId` 가 있어야 되돌린다 — `INIT_MY_INFO.language` 가 ko 라서, 로그아웃·
      // 토큰만료로 `MyInfoState` 가 초기화된 것과 사용자가 ko 를 고른 것을 language 만으론
      // 구분할 수 없다. 가드가 없으면 영어 사용자가 만료되는 순간 화면이 한국어로 뒤집히고,
      // 이동하는 `/login` 은 미등록 라우트라 되돌려주지도 않는다.
      settingLanguage = 'ko';
    }

    //RN 외국어모드로 오픈시 최우선권
    if (rnLanguageIsFirst && rnLanguage === 'en') {
      settingLanguage = 'en';
    }

    // 같은 언어여도 i18next 는 `languageChanged` 를 그대로 emit 해서
    // `useTranslation` 소비처가 전부 리렌더된다. 라우트 이동마다 effect 가 도는 구조라
    // (deps 의 `pathname`) 실제로 바뀔 때만 호출한다.
    if (settingLanguage && i18n.language !== settingLanguage) {
      i18n.changeLanguage(settingLanguage);
    }
  }, [
    language,
    passportId,
    rnLanguage,
    isZhEn,
    rnLanguageIsFirst,
    enabled,
    revertToKo,
    pathname,
  ]);
};

export default useLanguageSetting;
